import React, { useEffect, useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Loader2, FileSearch, AlertCircle } from "lucide-react";
import { readPriorsFile } from '@/lib/priorsReader';
import { formatHearing, priorsToFields } from '@/lib/priorsParser';

/**
 * Reads an uploaded priors document and offers its summary for question 24.
 * @param {{
 *   file: File | null,
 *   data: Record<string, any>,
 *   onChange: (name: string, value: any) => void,
 *   onDone: () => void
 * }} props
 */
export default function PriorsReaderPanel({ file, data, onChange, onDone }) {
  const [status, setStatus] = useState("idle"); // idle | reading | done | error
  const [progress, setProgress] = useState({ page: 0, total: 0 });
  const [hearings, setHearings] = useState([]);
  const [stopped, setStopped] = useState(false);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!file) return undefined;
    let cancelled = false;
    stopRef.current = false;
    setStatus("reading");
    setStopped(false);
    setHearings([]);
    setProgress({ page: 0, total: 0 });

    readPriorsFile(file, {
      shouldStop: () => stopRef.current || cancelled,
      onPage: (page, total, found) => {
        if (cancelled) return;
        setProgress({ page, total });
        setHearings([...found]);
      },
    })
      .then((result) => {
        if (cancelled) return;
        setHearings([...result.hearings]);
        setStopped(result.stopped);
        setProgress({ page: result.pagesRead, total: result.totalPages });
        setStatus("done");
      })
      .catch((err) => {
        console.error("Error reading priors:", err);
        if (!cancelled) setStatus("error");
      });

    return () => { cancelled = true; };
  }, [file]);

  if (!file || status === "idle") return null;

  const fields = priorsToFields(hearings);
  const hasExisting = Boolean((data.convictions_details || "").trim());

  const apply = (mode) => {
    const existing = (data.convictions_details || "").trim();
    // Only the outline box: the Year / Offence / Penalty boxes are left for the lawyer.
    onChange("convictions_details", mode === "append" && existing ? `${existing}\n${fields.details}` : fields.details);
    onDone();
  };

  if (status === "reading") {
    return (
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {progress.total
              ? `Reading priors – page ${progress.page} of ${progress.total} · ${fields.count} court ${fields.count === 1 ? "hearing" : "hearings"} found`
              : "Opening the priors document..."}
          </span>
          {progress.total > 1 && (
            <Button type="button" variant="outline" size="sm" className="bg-white" onClick={() => { stopRef.current = true; }}>
              Stop here
            </Button>
          )}
        </div>
        <p className="text-xs text-blue-700">
          The newest hearings come first, so you can stop once you have enough. The document is read on this computer and isn't sent anywhere.
        </p>
      </div>
    );
  }

  if (status === "error" || fields.count === 0) {
    return (
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <p>The priors couldn't be read from this document, so please type them in above.</p>
          <p className="text-xs text-amber-700 mt-1">This works with Victoria Police LEAP criminal records. The document is still attached to the application.</p>
          <Button type="button" variant="ghost" size="sm" className="mt-1 h-7 px-2" onClick={onDone}>Dismiss</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 space-y-3">
      <p className="flex items-center gap-2 font-medium">
        <FileSearch className="w-4 h-4" />
        Found {fields.count} court {fields.count === 1 ? "hearing" : "hearings"}
        {stopped ? ` (stopped after page ${progress.page} of ${progress.total})` : ""}, newest first
      </p>
      <ul className="max-h-64 overflow-y-auto bg-white border border-blue-100 rounded-md divide-y divide-blue-50 text-xs text-gray-800">
        {hearings.filter((h) => h.charges.length).map((h, i) => (
          <li key={i} className="px-3 py-2">{formatHearing(h)}</li>
        ))}
      </ul>
      <p className="text-xs text-blue-700">Check these against the document before using them. You can edit the text once it's added.</p>
      <div className="flex flex-wrap gap-2">
        {hasExisting ? (
          <>
            <Button type="button" size="sm" onClick={() => apply("replace")}>Replace what's written</Button>
            <Button type="button" size="sm" variant="outline" className="bg-white" onClick={() => apply("append")}>Add below what's written</Button>
          </>
        ) : (
          <Button type="button" size="sm" onClick={() => apply("replace")}>Add to question 24</Button>
        )}
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>Don't use</Button>
      </div>
    </div>
  );
}
